import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { AlertCircle, CheckCircle2, Info, AlertTriangle, BellOff, CheckCheck, Trash2, Bell } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ICONS = {
  warning: <AlertTriangle className="h-5 w-5 text-amber-500" />,
  success: <CheckCircle2 className="h-5 w-5 text-emerald-500" />,
  info: <Info className="h-5 w-5 text-blue-500" />,
  error: <AlertCircle className="h-5 w-5 text-red-500" />,
};

const CATEGORY_STYLES = {
  renewals: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  invoices: 'bg-red-500/10 text-red-400 border-red-500/30',
  leaves: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
  tickets: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
  payments: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/notifications');
      if (r.data.success) setNotifs(r.data.data || []);
    } catch (e) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const unreadCount = notifs.filter(n => !n.read).length;

  const handleClick = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (_e) { /* non-blocking */ }
    }
    if (n.link) navigate(n.link);
  };

  const handleMarkAllRead = async () => {
    if (unreadCount === 0) return;
    setBusy(true);
    try {
      await api.post('/notifications/read-all');
      toast.success(`Marked ${unreadCount} notification(s) as read`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark all read');
    } finally { setBusy(false); }
  };

  const handleClearAll = async () => {
    if (notifs.length === 0) return;
    if (!window.confirm(`Clear all ${notifs.length} notification(s)? Genuinely-new items will still appear later, but the current list will be dismissed from your bell.`)) return;
    setBusy(true);
    try {
      const r = await api.post('/notifications/clear-all');
      toast.success(r.data?.message || 'All notifications cleared');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear notifications');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="notifications-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6 text-primary" />
            Notifications
          </h1>
          <p className="text-sm text-muted-foreground">
            All your live alerts in one place — pending leaves, overdue invoices, renewals, and more.
            {unreadCount > 0 && <span className="ml-2 text-primary font-medium">{unreadCount} unread</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleMarkAllRead}
            disabled={busy || unreadCount === 0}
            data-testid="notif-page-mark-all-read"
          >
            <CheckCheck className="h-4 w-4 mr-2" />
            Mark all read
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearAll}
            disabled={busy || notifs.length === 0}
            className="text-destructive border-destructive/30 hover:bg-destructive/10"
            data-testid="notif-page-clear-all"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear all
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : notifs.length === 0 ? (
        <Card data-testid="notif-empty-state">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <BellOff className="h-12 w-12 text-muted-foreground/40 mb-4" />
            <p className="text-base font-medium text-muted-foreground">You&apos;re all caught up</p>
            <p className="text-sm text-muted-foreground/70 mt-1">No pending alerts right now.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {notifs.map(n => (
            <button
              key={n.id}
              onClick={() => handleClick(n)}
              className={`flex items-start gap-4 w-full px-4 py-3.5 text-left rounded-lg border transition-colors ${
                !n.read
                  ? 'bg-primary/5 border-primary/20 hover:bg-primary/10'
                  : 'bg-card border-border hover:bg-muted/40'
              }`}
              data-testid={`notif-row-${n.id}`}
            >
              <div className="mt-0.5 shrink-0">{ICONS[n.type] || ICONS.info}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-sm ${!n.read ? 'font-semibold' : 'font-medium'}`}>{n.title}</p>
                  <Badge variant="outline" className={`text-[10px] py-0 px-1.5 ${CATEGORY_STYLES[n.category] || ''}`}>
                    {n.category}
                  </Badge>
                  {!n.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" data-testid="notif-unread-dot" />}
                </div>
                <p className="text-sm text-muted-foreground mt-1">{n.message}</p>
                <p className="text-xs text-muted-foreground/60 mt-1">{n.time}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
